import React, {memo, useCallback, useEffect, useMemo, useRef, CSSProperties, DragEvent, MouseEvent} from 'react';
import type {EditableTableRowProps} from "@/@types/editableTable";
import IconButton from "../IconButton/IconButton";
import Icons from "../IconButton/Icons";
import Loading from "../Loading";
import {useInView} from "react-intersection-observer";

/**
 * Latest callback props, delivered to the memoized inner row through a stable
 * ref. The memo comparator below deliberately skips handler props (rows are
 * memoized for table render performance, and the parent recreates handler
 * closures every render). But skipping the re-render also freezes the handler
 * props at their last-rendered values — those closures capture stale
 * EditableTable state (e.g. handleDelete's selectedRows/propItems), the same
 * defect class as OWA-516's TextField. The unmemoized outer wrapper refreshes
 * this ref on every parent render, so the row always dispatches to the
 * freshest closures (OWA-517). `otherActions` rides along so a click invokes
 * the CURRENT action object, not the render-frozen copy.
 */
type LatestRowCallbacks<T> = Pick<EditableTableRowProps<T>,
    'onBeginEdit' | 'onDelete' | 'onSelectRow' | 'onCallAction' |
    'onDragStart' | 'onDragEnter' | 'onDragExit' | 'onDragLeave' |
    'onDragEnd' | 'onDragOver' | 'onDrag' | 'onDrop' |
    'onRowVisible' | 'otherActions'>;

type EditableTableRowInnerProps<T> = EditableTableRowProps<T> & {
    latestRef: React.MutableRefObject<LatestRowCallbacks<T>>,
};

function EditableTableRowComponent<T>(props: EditableTableRowInnerProps<T>) {
    const {
        item,
        rowIndex,
        properties,
        itemId,
        entityName,
        isSelected,
        isDeleting,
        editing,
        width,
        canEdit,
        canDelete,
        canDeleteMessage,
        multiSelect,
        isDraggable,
        hideStandardActions,
        showReadOnlyIcon,
        evenOdd,
        bgStyle,
        rowStyleFn,
        otherActions,
        latestRef,
        anyButtons,
    } = props;

    // Memoize the intersection observer options to prevent recreation
    const observerOptions = useMemo(() => ({
        threshold: 0,
        triggerOnce: true,
    }), []);

    const { ref, inView } = useInView(observerOptions);

    // Notify parent when row becomes visible
    useEffect(() => {
        if (inView && latestRef.current.onRowVisible) {
            latestRef.current.onRowVisible(itemId);
        }
    }, [inView, itemId, latestRef]);

    const rowClass = `row_${itemId.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    const backgroundImage = bgStyle?.backgroundImage || '';
    const totalColumns = properties.filter(p => !p.exportOnly).length;

    // Add hover handlers to add/remove a hover class
    const handleMouseEnter = useCallback((e: MouseEvent) => {
        const grid = e.currentTarget.closest('.editable-table__grid');
        if (grid) {
            const cells = grid.querySelectorAll(`.${rowClass}`);
            cells.forEach(cell => cell.classList.add('row-hovered'));
        }
    }, [rowClass]);

    const handleMouseLeave = useCallback((e: MouseEvent) => {
        const grid = e.currentTarget.closest('.editable-table__grid');
        if (grid) {
            const cells = grid.querySelectorAll(`.${rowClass}`);
            cells.forEach(cell => cell.classList.remove('row-hovered'));
        }
    }, [rowClass]);

    // Stable callbacks that include item. All dispatch through latestRef so a
    // memo-suppressed re-render can never leave the DOM holding a handler
    // closure from an older EditableTable render (OWA-517).
    const handleEditClick = useCallback((e: MouseEvent) => {
        latestRef.current.onBeginEdit(e, item, false);
    }, [latestRef, item]);

    const handleViewClick = useCallback((e: MouseEvent) => {
        latestRef.current.onBeginEdit(e, item, true);
    }, [latestRef, item]);

    const handleDeleteClick = useCallback((e: MouseEvent) => {
        latestRef.current.onDelete(e, item);
    }, [latestRef, item]);

    const handleSelectClick = useCallback(() => {
        latestRef.current.onSelectRow(item);
    }, [latestRef, item]);

    // Drag handlers
    const handleDragStart = useCallback((e: DragEvent) => {
        latestRef.current.onDragStart?.(e, item);
    }, [latestRef, item]);

    const handleDragEnter = useCallback((e: DragEvent) => {
        latestRef.current.onDragEnter?.(e, item);
    }, [latestRef, item]);

    const handleDragExit = useCallback((e: DragEvent) => {
        latestRef.current.onDragExit?.(e, item);
    }, [latestRef, item]);

    const handleDragLeave = useCallback((e: DragEvent) => {
        latestRef.current.onDragLeave?.(e, item);
    }, [latestRef, item]);

    const handleDragEnd = useCallback((e: DragEvent) => {
        latestRef.current.onDragEnd?.(e, item);
    }, [latestRef, item]);

    const handleDragOver = useCallback((e: DragEvent) => {
        latestRef.current.onDragOver?.(e, item);
    }, [latestRef, item]);

    const handleDrag = useCallback((e: DragEvent) => {
        latestRef.current.onDrag?.(e, item);
    }, [latestRef, item]);

    const handleDrop = useCallback((e: DragEvent) => {
        latestRef.current.onDrop?.(e, item);
    }, [latestRef, item]);

    // Memoize all cell styles at once - only recalculate when dependencies change
    const cellStyles = useMemo((): CSSProperties[] => {
        if (!rowStyleFn) {
            // If no rowStyleFn, just return overflow styles for single-line cells
            return properties
                .filter(p => !p.exportOnly)
                .map((prop) => prop.useSingleLine ? {overflow: 'hidden'} : {});
        }

        return properties
            .filter(p => !p.exportOnly)
            .map((prop, columnIndex) => {
                const baseStyle = rowStyleFn(item, columnIndex, totalColumns);

                // Apply overflow if needed
                if (prop.useSingleLine) {
                    return {...baseStyle, overflow: 'hidden'};
                }

                return baseStyle;
            });
    }, [item, properties, rowStyleFn, totalColumns]);

    // Style for the trailing action-buttons cell — must match the row's data cells
    // so the background color (driven by rowStyleFn) extends through the action column.
    const actionCellStyle = useMemo((): CSSProperties => {
        return rowStyleFn ? rowStyleFn(item, totalColumns, totalColumns) : {};
    }, [item, rowStyleFn, totalColumns]);

    // Memoize wrapper styles for single-line and centered cells
    const wrapperStyles = useMemo(() => {
        return properties
            .filter(p => !p.exportOnly)
            .map((prop) => {
                // Inline centering styles override CSS specificity conflicts from page-level LESS
                const centerStyle: CSSProperties | undefined = prop.justify === 'center' ? {
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    width: 'fit-content',
                    flexGrow: 0,
                    textAlign: 'center',
                } : undefined;

                if (prop.useSingleLine) {
                    return {
                        ...centerStyle,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-evenly',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        maxWidth: '100%'
                    } as CSSProperties;
                }

                return centerStyle;
            });
    }, [properties]);

    // Memoize rendered cell values - only call renderItemFn when in view
    const cellValues = useMemo(() => {
        return properties
            .filter(p => !p.exportOnly)
            .map((prop, columnIndex) => {
                // If there's a renderItemFn but row is not in view, return null to avoid rendering
                if (!inView) return <div key={`wrapper_${rowIndex}_${columnIndex}`}/>;

                const v = prop.renderItemFn ?
                          prop.renderItemFn(item, prop.label, false, width) :
                          item[prop.label as keyof T];

                const justify = prop.justify ? `editable-table--justify-${prop.justify}` : '';
                const columnClass = `column--${prop.label}`;
                const wrapperStyle = wrapperStyles[columnIndex];

                if (prop.renderItemFn || typeof v === 'string') {
                    return (
                        <div className={`editable-table__wrapper ${justify} ${columnClass}`}
                             style={wrapperStyle}
                             key={`wrapper_${rowIndex}_${columnIndex}`}>
                            {v}
                        </div>
                    );
                }
                return v;
            });
    }, [item, properties, width, rowIndex, wrapperStyles, inView]);

    // When row is not in view, render a placeholder that spans all columns
    // This prevents checkboxes from appearing before data cells load
    // IMPORTANT: This check must come AFTER all hooks to satisfy Rules of Hooks
    if (!inView) {
        return (
            <div ref={ref}
                 className={`editable-table__cell editable-table--placeholder ${evenOdd} ${rowClass}`}
                 style={{gridColumn: '1 / -1', minHeight: '2.5em'}}>
                {/* Empty placeholder maintains row height */}
            </div>
        );
    }

    const cells = [];

    // Multi-select checkbox
    if (multiSelect) {
        cells.push(
            <div key={`checkbox_${rowIndex}`}
                 data-row-id={itemId}
                 data-col-label="__checkbox__"
                 onMouseEnter={handleMouseEnter}
                 onMouseLeave={handleMouseLeave}
                 className={`editable-table__cell editable-table--rounded-corners-start center ${evenOdd} ${rowClass}`.trim()}>
                <IconButton
                    type={isSelected ? Icons.CHECKED : Icons.UNCHECKED}
                    onClick={handleSelectClick}
                />
            </div>
        );
    }

    // Data cells - now using memoized values and styles
    properties
        .filter(p => !p.exportOnly)
        .forEach((prop, columnIndex) => {
            const columnClass = `column--${prop.label}`;
            const style = cellStyles[columnIndex]; // No need for || {} since we always return an object
            const value = cellValues[columnIndex];

            // No more mutation needed!

            const editableClass = !editing && !prop.noClick && canEdit && !isDeleting ?
                                  'editable-table--editable' : '';
            const justifyClass = prop.justify ? `editable-table--justify-${prop.justify}` : '';
            const noFormatting = prop.noCellMargins ? 'no-margin' : '';
            const specialFormatting = multiSelect
                                      ? columnIndex === totalColumns
                                        ? 'editable-table--rounded-corners-end'
                                        : ''
                                      : columnIndex === 0
                                        ? 'editable-table--rounded-corners-start'
                                        : columnIndex === totalColumns - 1
                                          ? 'editable-table--rounded-corners-end'
                                          : '';

            cells.push(
                <div key={`cell_${rowIndex}_${columnIndex}`}
                     ref={columnIndex === 0 ? ref : undefined} // Attach useInView ref to first data cell
                     data-row-id={itemId}
                     data-col-label={prop.label}
                     onMouseEnter={handleMouseEnter}
                     onMouseLeave={handleMouseLeave}
                     style={style}
                     className={`editable-table__cell ${evenOdd} ${rowClass} ${editableClass} ${justifyClass} ${noFormatting} ${backgroundImage} ${specialFormatting}`.trim()}
                     onClick={!editing && !prop.noClick && canEdit ? handleEditClick : undefined}
                     draggable={!prop.disableDrag && isDraggable ? true : undefined}
                     onDragStart={!prop.disableDrag && isDraggable ? handleDragStart : undefined}
                     onDragEnter={!prop.disableDrag && isDraggable ? handleDragEnter : undefined}
                     onDragExit={!prop.disableDrag && isDraggable ? handleDragExit : undefined}
                     onDragLeave={!prop.disableDrag && isDraggable ? handleDragLeave : undefined}
                     onDragEnd={!prop.disableDrag && isDraggable ? handleDragEnd : undefined}
                     onDragOver={!prop.disableDrag && isDraggable ? handleDragOver : undefined}
                     onDrag={!prop.disableDrag && isDraggable ? handleDrag : undefined}
                     onDrop={!prop.disableDrag && isDraggable ? handleDrop : undefined}>
                    {value}
                </div>
            );
        });

    // Action buttons - build the list first, then only add cell if there are buttons
    const showActions = !editing;
    if (showActions) {
        const actionButtons = [];

        // Other custom actions
        if (otherActions) {
            otherActions
                .filter(oa => !oa.multiOnly && (!oa.showIf || oa.showIf(item)))
                .forEach(oa => {
                    const icon = oa.selectedIconFn ? oa.selectedIconFn(item) : oa.selectedIcon;
                    actionButtons.push(
                        <IconButton
                            key={`action_${oa.label}`}
                            type={icon}
                            title={oa.selectedText || oa.displayLabel || oa.label}
                            color={oa.color || 'black'}
                            textColor={oa.color || 'black'}
                            onClick={() => {
                                // Re-resolve the action at click time: the rendered
                                // `oa` may be a memo-frozen copy whose `action`
                                // closure captured stale parent state (OWA-517).
                                const latest = latestRef.current;
                                const fresh = latest.otherActions?.find(a => a.label === oa.label) ?? oa;
                                latest.onCallAction(fresh, item);
                            }}
                        />
                    );
                });
        }

        // Standard actions
        if (showReadOnlyIcon && canEdit) {
            actionButtons.push(
                <IconButton
                    key="view"
                    type={Icons.VIEW}
                    title="View"
                    onClick={handleViewClick}
                />
            );
        }

        if (!hideStandardActions && canEdit) {
            actionButtons.push(
                <IconButton
                    key="edit"
                    type={Icons.EDIT}
                    title="Edit"
                    onClick={handleEditClick}
                />
            );
        }

        if (!hideStandardActions && isDeleting && isSelected) {
            actionButtons.push(
                <Loading
                    key="deleting"
                    id={`EditableTable-${entityName}-${rowIndex}-Deleting`}
                    inline={true}
                />
            );
        }

        if (!hideStandardActions && canDelete) {
            actionButtons.push(
                <IconButton
                    key="delete"
                    type={Icons.DELETE}
                    title={canDeleteMessage || "Delete"}
                    onClick={handleDeleteClick}
                    disabled={!canDelete || isDeleting}
                />
            );
        }

        // Always add the action cell if the table has an action column (anyButtons),
        // even if this particular row has no buttons. This maintains grid alignment.
        if (anyButtons) {
            cells.push(
                <div key={`actions_${rowIndex}`}
                     data-row-id={itemId}
                     data-col-label="__buttons__"
                     onMouseEnter={handleMouseEnter}
                     onMouseLeave={handleMouseLeave}
                     style={actionCellStyle}
                     className={`editable-table__cell Buttons ${evenOdd} ${rowClass} ${backgroundImage}`}>
                    {actionButtons}
                </div>
            );
        }
    }

    return <>{cells}</>;
}

// Improve the memoization comparison.
// Handler props are deliberately NOT compared — the parent recreates those
// closures every render, so comparing them would defeat the row memoization
// entirely. The inner component dispatches handlers through `latestRef`
// (refreshed by the unmemoized wrapper below), so the skipped re-renders
// cannot freeze stale closures (OWA-517).
const EditableTableRowInner = memo(EditableTableRowComponent, (prevProps, nextProps) => {
    // Check if properties array changed (by reference)
    if (prevProps.properties !== nextProps.properties) return false;

    // Check if rowStyleFn changed (by reference)
    if (prevProps.rowStyleFn !== nextProps.rowStyleFn) return false;

    // Check primitive values
    return (
        prevProps.item === nextProps.item &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.isDeleting === nextProps.isDeleting &&
        prevProps.canEdit === nextProps.canEdit &&
        prevProps.canDelete === nextProps.canDelete &&
        prevProps.width === nextProps.width &&
        prevProps.editing === nextProps.editing &&
        prevProps.viewOnly === nextProps.viewOnly &&
        prevProps.rowIndex === nextProps.rowIndex &&
        prevProps.anyButtons === nextProps.anyButtons
    );
}) as typeof EditableTableRowComponent;

/**
 * Unmemoized outer wrapper: refreshes `latestRef` with the newest handler
 * props (and `otherActions`) on every parent render, then renders the
 * memoized inner row. The memo still suppresses row re-renders for table
 * performance; the ref makes sure the suppressed renders can't freeze stale
 * handler closures (OWA-517 — same fix shape as OWA-516's TextField).
 */
export function EditableTableRow<T>(props: EditableTableRowProps<T>) {
    const latestRef = useRef<LatestRowCallbacks<T>>({} as LatestRowCallbacks<T>);
    latestRef.current = {
        onBeginEdit: props.onBeginEdit,
        onDelete: props.onDelete,
        onSelectRow: props.onSelectRow,
        onCallAction: props.onCallAction,
        onDragStart: props.onDragStart,
        onDragEnter: props.onDragEnter,
        onDragExit: props.onDragExit,
        onDragLeave: props.onDragLeave,
        onDragEnd: props.onDragEnd,
        onDragOver: props.onDragOver,
        onDrag: props.onDrag,
        onDrop: props.onDrop,
        onRowVisible: props.onRowVisible,
        otherActions: props.otherActions,
    };

    return <EditableTableRowInner {...props} latestRef={latestRef}/>;
}
